import { apiRequest } from "@/api/client";
import { getAdminToken } from "./auth";
import type {
  AdminAgeGroup,
  AdminAgeGroupPayload,
  AdminCategory,
  AdminCategoryPayload,
  AdminChild,
  AdminMapping,
  AdminOverview,
  AdminProfile,
  AdminReport,
  AdminSkill,
  AdminSkillPayload,
  AdminUser,
  AdminUserPayload,
  AdminUserUpdatePayload,
  SiteSettings,
  StorePaymentMethod,
  StoreShippingCompany,
  AdminParent,
  AdminProduct,
  AdminProductResponse,
  AdminCheckoutRequest,
  ColumnMapping,
  ImportParseResponse,
  ImportResult,
} from "./types";

export const adminLogin = (payload: { email: string; password: string }) => {
  return apiRequest<{ token: string; admin: AdminProfile }>("/admin/auth/login", {
    method: "POST",
    body: payload,
  });
};

export const fetchAdminProfile = () => {
  return apiRequest<AdminProfile>("/admin/auth/me", {
    token: getAdminToken(),
  });
};

export const fetchAdminOverview = () => {
  return apiRequest<AdminOverview>("/admin/overview", {
    token: getAdminToken(),
  });
};

export const fetchAdminCheckoutRequests = () => {
  return apiRequest<AdminCheckoutRequest[]>("/admin/checkout-requests", {
    token: getAdminToken(),
  });
};

export const fetchAdminAgeGroups = () => {
  return apiRequest<AdminAgeGroup[]>("/admin/age-groups", {
    token: getAdminToken(),
  });
};

export const createAdminAgeGroup = (payload: AdminAgeGroupPayload) => {
  return apiRequest<AdminAgeGroup>("/admin/age-groups", {
    method: "POST",
    body: payload,
    token: getAdminToken(),
  });
};

export const updateAdminAgeGroup = (id: number, payload: Partial<AdminAgeGroupPayload>) => {
  return apiRequest<AdminAgeGroup>(`/admin/age-groups/${id}`,
    {
      method: "PUT",
      body: payload,
      token: getAdminToken(),
    },
  );
};

export const updateAdminAgeGroupStatus = (id: number, isActive: boolean) => {
  return apiRequest<AdminAgeGroup>(`/admin/age-groups/${id}/status`, {
    method: "PATCH",
    body: { isActive },
    token: getAdminToken(),
  });
};

export const fetchAdminCategories = () => {
  return apiRequest<AdminCategory[]>("/admin/categories", {
    token: getAdminToken(),
  });
};

export const createAdminCategory = (payload: AdminCategoryPayload) => {
  return apiRequest<AdminCategory>("/admin/categories", {
    method: "POST",
    body: payload,
    token: getAdminToken(),
  });
};

export const updateAdminCategory = (id: number, payload: Partial<AdminCategoryPayload>) => {
  return apiRequest<AdminCategory>(`/admin/categories/${id}`,
    {
      method: "PUT",
      body: payload,
      token: getAdminToken(),
    },
  );
};

export const updateAdminCategoryStatus = (id: number, isActive: boolean) => {
  return apiRequest<AdminCategory>(`/admin/categories/${id}/status`, {
    method: "PATCH",
    body: { isActive },
    token: getAdminToken(),
  });
};

export const fetchAdminSkills = () => {
  return apiRequest<AdminSkill[]>("/admin/skills", {
    token: getAdminToken(),
  });
};

export const createAdminSkill = (payload: AdminSkillPayload) => {
  return apiRequest<AdminSkill>("/admin/skills", {
    method: "POST",
    body: payload,
    token: getAdminToken(),
  });
};

export const updateAdminSkill = (id: string, payload: Partial<AdminSkillPayload>) => {
  return apiRequest<AdminSkill>(`/admin/skills/${id}`,
    {
      method: "PUT",
      body: payload,
      token: getAdminToken(),
    },
  );
};

export const updateAdminSkillStatus = (id: string, isActive: boolean) => {
  return apiRequest<AdminSkill>(`/admin/skills/${id}/status`, {
    method: "PATCH",
    body: { isActive },
    token: getAdminToken(),
  });
};

export type AdminMediaItem = {
  name: string;
  type: "svg" | "image" | "other";
  url: string;
};

export const fetchAdminMedia = (type?: "svg" | "image") => {
  return apiRequest<AdminMediaItem[]>("/admin/media", {
    params: { type },
    token: getAdminToken(),
  });
};

export const uploadAdminMedia = async (file: File) => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3001/api/v1";
  const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
  const url = `${normalizedBaseUrl}/admin/media`;
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getAdminToken()}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    const error = payload?.error || "request_failed";
    throw new Error(error);
  }

  const payload = (await response.json()) as { data?: AdminMediaItem };
  return payload.data as AdminMediaItem;
};

export const fetchAdminProducts = (
  page: number = 1,
  limit: number = 10,
  search?: string,
  sortBy?: string,
  sortDirection?: "asc" | "desc"
) => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });
  
  if (search) params.append("search", search);
  if (sortBy) params.append("sortBy", sortBy);
  if (sortDirection) params.append("sortDirection", sortDirection);

  return apiRequest<AdminProductResponse>(`/admin/products?${params.toString()}`, {
    token: getAdminToken(),
  });
};

export const deleteAdminProduct = (id: string) => {
  return apiRequest<{ deleted: boolean }>(`/admin/products/${id}`, {
    method: "DELETE",
    token: getAdminToken(),
  });
};

export const updateAdminProduct = (
  id: string,
  payload: {
    sallaProductId: string;
    sku?: string | null;
    price: string;
    imageUrl: string;
    stockQuantity: number;
    nameAr: string;
    nameEn: string;
  }
) => {
  return apiRequest<AdminProduct>(`/admin/products/${id}`, {
    method: "PUT",
    body: payload,
    token: getAdminToken(),
  });
};

export const uploadProductsExcel = async (file: File): Promise<ImportParseResponse> => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3001/api/v1";
  const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
  const url = `${normalizedBaseUrl}/admin/products/import/parse`;
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getAdminToken()}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    const error = payload?.error || "upload_failed";
    throw new Error(error);
  }

  const payload = (await response.json()) as { data?: ImportParseResponse };
  return payload.data as ImportParseResponse;
};

export const executeProductsImport = (filePath: string, mapping: ColumnMapping[]) => {
  return apiRequest<ImportResult>("/admin/products/import/execute", {
    method: "POST",
    body: { filePath, mapping },
    token: getAdminToken(),
  });
};

export const syncProductsFromSalla = (payload?: {
  storeId?: string;
  markMissingOutOfStock?: boolean;
}) => {
  return apiRequest<{
    storeId: string;
    fetched: number;
    upserted: number;
    skipped: number;
    markedOutOfStock: number;
    errors: string[];
  }>("/admin/products/sync/salla", {
    method: "POST",
    body: payload || {},
    token: getAdminToken(),
  });
};



export const fetchAdminMappings = () => {
  return apiRequest<AdminMapping[]>("/admin/mappings", {
    token: getAdminToken(),
  });
};

export const createAdminMapping = (payload: {
  sallaProductId: string;
  skillId?: string;
  skillIds?: string[];
}) => {
  return apiRequest<any>("/admin/mappings", {
    method: "POST",
    body: payload,
    token: getAdminToken(),
  });
};

export const deleteAdminMapping = (id: string) => {
  return apiRequest<{ success: boolean }>(`/admin/mappings/${id}`, {
    method: "DELETE",
    token: getAdminToken(),
  });
};

export const updateAdminMapping = (
  id: string,
  payload: {
    sallaProductId?: string;
    skillId?: string;
  }
) => {
  return apiRequest<AdminMapping>(`/admin/mappings/${id}`, {
    method: "PUT",
    body: payload,
    token: getAdminToken(),
  });
};


export const fetchAdminParents = () => {
  return apiRequest<AdminParent[]>("/admin/parents", {
    token: getAdminToken(),
  });
};

export const fetchAdminChildren = () => {
  return apiRequest<AdminChild[]>("/admin/children", {
    token: getAdminToken(),
  });
};

export const fetchAdminReports = () => {
  return apiRequest<AdminReport[]>("/admin/reports", {
    token: getAdminToken(),
  });
};

export const fetchAdminSettings = () => {
  return apiRequest<SiteSettings>("/admin/settings", {
    token: getAdminToken(),
  });
};

export const updateAdminSettings = (payload: Partial<SiteSettings>) => {
  return apiRequest<SiteSettings>("/admin/settings", {
    method: "PUT",
    body: payload,
    token: getAdminToken(),
  });
};

export const testAdminSmtp = (toEmail: string) => {
  return apiRequest<{ ok: boolean }>("/admin/settings/smtp-test", {
    method: "POST",
    body: { toEmail },
    token: getAdminToken(),
  });
};

export const fetchSallaSyncCategories = () => {
  return apiRequest<{ id: string; name: string }[]>("/admin/settings/salla/categories", {
    token: getAdminToken(),
  });
};

export const fetchSallaPaymentMethods = () => {
  return apiRequest<StorePaymentMethod[]>("/admin/settings/salla/payment-methods", {
    token: getAdminToken(),
  });
};

export const fetchSallaShippingCompanies = () => {
  return apiRequest<StoreShippingCompany[]>("/admin/settings/salla/shipping-companies", {
    token: getAdminToken(),
  });
};

export const fetchSallaConnectionStatus = () => {
  return apiRequest<{
    connected: boolean;
    installation?: {
      storeId: string;
      storeName?: string | null;
      storeDomain?: string | null;
      updatedAt: string;
    } | null;
  }>("/admin/settings/salla/status", {
    token: getAdminToken(),
  });
};

export const disconnectSallaConnection = () => {
  return apiRequest<{ ok: boolean }>("/admin/settings/salla/connection", {
    method: "DELETE",
    token: getAdminToken(),
  });
};

export const fetchAdminUsers = () => {
  return apiRequest<AdminUser[]>("/admin/admins", {
    token: getAdminToken(),
  });
};

export const createAdminUser = (payload: AdminUserPayload) => {
  return apiRequest<AdminUser>("/admin/admins", {
    method: "POST",
    body: payload,
    token: getAdminToken(),
  });
};

export const updateAdminUser = (id: string, payload: AdminUserUpdatePayload) => {
  return apiRequest<AdminUser>(`/admin/admins/${id}`,
    {
      method: "PUT",
      body: payload,
      token: getAdminToken(),
    },
  );
};
